import { useEffect, useRef, type MouseEvent, type PointerEvent } from 'react';

/** Hold time before a touch counts as a long-press (Android/iOS use ~500 ms). */
export const LONG_PRESS_MS = 500;
/** Finger movement (px) that turns the press into a scroll and cancels it. */
const MOVE_TOLERANCE_PX = 10;

export interface LongPressPoint {
  target: Element;
  x: number;
  y: number;
}

export interface LongPressHandlers {
  onPointerDown: (event: PointerEvent) => void;
  onPointerMove: (event: PointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  /** Swallows the click that follows a long-press, so the item does not also open. */
  onClickCapture: (event: MouseEvent) => void;
}

/**
 * Long-press for touch and pen (DESIGN.md §18): calls `onLongPress` after `LONG_PRESS_MS` without
 * moving. Mouse input is ignored (right-click covers it). `isPressing()` is true from touch-down
 * until shortly after the long-press, so a native `contextmenu` event of the same gesture (Android)
 * can be ignored.
 */
export function useLongPress(onLongPress: (point: LongPressPoint) => void): LongPressHandlers & { isPressing: () => boolean } {
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  const start = useRef<{ x: number; y: number }>(undefined);
  const fired = useRef(false);
  const callback = useRef(onLongPress);
  useEffect(() => {
    callback.current = onLongPress;
  });
  useEffect(() => () => clearTimeout(timer.current), []);

  const cancel = () => {
    clearTimeout(timer.current);
    timer.current = undefined;
    start.current = undefined;
  };

  return {
    onPointerDown(event) {
      if (event.pointerType === 'mouse') return;
      cancel();
      fired.current = false;
      const target = event.target as Element;
      const point = { x: event.clientX, y: event.clientY };
      start.current = point;
      timer.current = setTimeout(() => {
        timer.current = undefined;
        fired.current = true;
        callback.current({ target, ...point });
      }, LONG_PRESS_MS);
    },
    onPointerMove(event) {
      const origin = start.current;
      if (origin && Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > MOVE_TOLERANCE_PX) cancel();
    },
    onPointerUp: cancel,
    onPointerCancel: cancel,
    onClickCapture(event) {
      if (!fired.current) return;
      fired.current = false;
      event.preventDefault();
      event.stopPropagation();
    },
    isPressing: () => timer.current !== undefined || fired.current,
  };
}
